import type { PlanType } from '@/types/database';

export type FeatureAccess = 'allowed' | 'blocked' | 'upsell';

export type FeatureId =
  | 'unlimited_quotes'
  | 'multiple_companies'
  | 'brand_color'
  | 'pdf_templates'
  | 'reports_advanced'
  | 'reports_export'
  | 'quote_photos'
  | 'no_ads'
  | 'no_branding_footer'
  | 'fiscal_data'
  | 'boleto_scanner'
  | 'xls_export'
  | 'financial_module'
  | 'nfse_emission'
  | 'team_accounts'
  | 'service_orders'
  | 'agenda'
  | 'contracts'
  | 'receipts'
  | 'inventory'
  | 'product_variants'
  | 'message_templates'
  | 'document_signature'
  | 'thermal_printing'
  | 'auto_payment_detection';

export type FeatureUpsellReason =
  | 'limit'
  | 'feature'
  | 'companies'
  | 'photos'
  | 'ads'
  | 'reports'
  | 'color';

export interface FeatureRule {
  id: FeatureId;
  label: string;
  description: string;
  free: FeatureAccess;
  pro: FeatureAccess;
  admin: FeatureAccess;
  upsellReason?: FeatureUpsellReason;
}

export const FEATURES = {
  unlimited_quotes: {
    id: 'unlimited_quotes',
    label: 'Orçamentos ilimitados',
    description: 'Remove a cota mensal de orçamentos do plano gratuito.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'limit',
  },
  multiple_companies: {
    id: 'multiple_companies',
    label: 'Múltiplas empresas',
    description: 'Permite cadastrar e alternar entre mais de uma empresa emissora.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'companies',
  },
  brand_color: {
    id: 'brand_color',
    label: 'Cor da marca',
    description: 'Personaliza a cor principal usada na identidade e nos documentos.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'color',
  },
  document_signature: {
    id: 'document_signature',
    label: 'Assinatura nos documentos',
    description:
      'Salva a assinatura do prestador uma vez e imprime em todos os orçamentos, pedidos e ordens de serviço.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  pdf_templates: {
    id: 'pdf_templates',
    label: 'Modelos de PDF',
    description: 'Libera os modelos de PDF além do modelo clássico.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  thermal_printing: {
    id: 'thermal_printing',
    label: 'Impressora térmica',
    description:
      'Gera o cupom em bobina de 58 ou 80 mm para orçamento, venda rápida, ordem de serviço e recibo.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  reports_advanced: {
    id: 'reports_advanced',
    label: 'Relatórios avançados',
    description: 'Exibe indicadores, gráficos, rankings e análises completas.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'reports',
  },
  reports_export: {
    id: 'reports_export',
    label: 'Exportar relatórios',
    description: 'Exporta os dados dos relatórios para arquivos externos.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'reports',
  },
  quote_photos: {
    id: 'quote_photos',
    label: 'Fotos e anexos',
    description:
      'Adiciona fotos aos itens e ao catálogo, anexos ao orçamento e comprovantes aos lançamentos do financeiro.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'photos',
  },
  no_ads: {
    id: 'no_ads',
    label: 'Sem anúncios',
    description: 'Remove anúncios e banners promocionais da experiência.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'ads',
  },
  no_branding_footer: {
    id: 'no_branding_footer',
    label: 'PDF sem marca do Orçamigo',
    description: 'Remove a assinatura do Orçamigo do rodapé dos documentos.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  fiscal_data: {
    id: 'fiscal_data',
    label: 'Consulta fiscal',
    description: 'Consulta tabelas LC 116, NBS e ISS municipal.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  boleto_scanner: {
    id: 'boleto_scanner',
    label: 'Leitor de boletos',
    description: 'Lê o código de barras e formata a linha digitável de boletos.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  xls_export: {
    id: 'xls_export',
    label: 'Exportar XLS',
    description: 'Exporta dados operacionais em formato compatível com Excel.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'reports',
  },
  financial_module: {
    id: 'financial_module',
    label: 'Módulo financeiro',
    description: 'Centraliza contas, recebimentos e acompanhamento financeiro.',
    free: 'allowed',
    pro: 'allowed',
    admin: 'allowed',
  },
  nfse_emission: {
    id: 'nfse_emission',
    label: 'Emissão de NFS-e',
    description: 'Emite notas fiscais de serviço pelo Orçamigo.',
    // Recurso PRO. O ambiente de emissão é uma trava SEPARADA: perfis fiscais
    // nascem em 'homolog' (sem efeito jurídico) e a produção real depende do
    // secret de servidor FISCAL_ALLOW_PROD (ausente) — ver _shared/nfse/prodgate.ts.
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  team_accounts: {
    id: 'team_accounts',
    label: 'Contas de equipe',
    description: 'Permite convidar pessoas e controlar o acesso por função.',
    free: 'blocked',
    pro: 'blocked',
    admin: 'allowed',
  },
  service_orders: {
    id: 'service_orders',
    label: 'Ordens de serviço',
    description: 'Registra e acompanha a execução dos serviços a partir dos orçamentos.',
    free: 'allowed',
    pro: 'allowed',
    admin: 'allowed',
  },
  agenda: {
    id: 'agenda',
    label: 'Agenda',
    description: 'Calendário que reúne compromissos e ordens de serviço agendadas.',
    // Grátis para todos: feature de retenção, sem paywall.
    free: 'allowed',
    pro: 'allowed',
    admin: 'allowed',
  },
  contracts: {
    id: 'contracts',
    label: 'Contratos',
    description: 'Cria contratos de prestação de serviço com assinatura digital remota.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  receipts: {
    id: 'receipts',
    label: 'Recibos',
    description: 'Emite recibos de pagamento com valor por extenso e assinatura do emitente.',
    // Grátis para todos: fecha o fluxo orçamento→contrato→OS→recibo, sem paywall.
    free: 'allowed',
    pro: 'allowed',
    admin: 'allowed',
  },
  inventory: {
    id: 'inventory',
    label: 'Estoque',
    description: 'Controla a quantidade em estoque dos produtos físicos e dá baixa nas vendas.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  product_variants: {
    id: 'product_variants',
    label: 'Produtos com variações',
    description: 'Cria variações do mesmo produto (P/M/G) com preço, código de barras e estoque próprios.',
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  message_templates: {
    id: 'message_templates',
    label: 'Modelos de mensagens',
    description:
      'Cria mensagens prontas com variáveis para enviar orçamentos, cobranças e contratos ao cliente.',
    // Só CRIAR/EDITAR modelos é PRO. O ENVIO em si é grátis: sem a flag, todo
    // ponto de envio usa a mensagem padrão de sempre (ver DEFAULT_TEMPLATES em
    // src/lib/messageTemplates.ts). Ninguém fica sem conseguir enviar.
    free: 'upsell',
    pro: 'allowed',
    admin: 'allowed',
    upsellReason: 'feature',
  },
  auto_payment_detection: {
    id: 'auto_payment_detection',
    label: 'Detecção automática de pagamento',
    description:
      'O orçamento leva um QR Pix exclusivo: quando o cliente paga, o app dá baixa no financeiro, avisa você e repassa o valor.',
    // ⚠️ BLOQUEADA PARA TODOS, DE PROPÓSITO — e isto NÃO é o gate de plano.
    //
    // Ligar o interruptor cria uma subconta REAL no Asaas, o que (a) dispara o
    // período de avaliação regulatória (10 subcontas / R$ 2.000 cada / 60 dias,
    // hurdle #165c) e (b) manda e-mail do Asaas ao prestador enquanto o BaaS não
    // estiver habilitado na conta-mãe. Nenhum dos dois é reversível por código.
    //
    // Até o BaaS ser habilitado em produção, alcança a feature quem tiver
    // `plan_type = 'admin'` (conta interna, ninguém chega nela assinando) ou um
    // `feature_overrides` com `access='allowed'` (hurdle #110) — os dois
    // caminhos de teste, nenhum deles passando por billing.
    //
    // `admin: 'allowed'` NÃO é descuido de segurança: é a invariante do registro
    // inteiro ("admin sempre libera", provada em `features.test.ts`), e quebrá-la
    // aqui só criaria uma exceção para a conta que precisa testar a feature.
    //
    // ⚠️ A CONDIÇÃO PARA DESTRAVAR NÃO É O LANÇAMENTO — É O BaaS APROVADO.
    // (Este comentário já disse "no lançamento, troque para PRO"; era instrução
    // errada e foi corrigida em 2026-08-07.) O app LANÇA com a feature travada.
    //
    // O BaaS do Asaas depende de habilitação manual na conta-mãe, e o CNPJ do
    // Orçamigo é MEI — que não tem CNAE de desenvolvimento de software, então a
    // aprovação é improvável enquanto não houver migração para ME/SLU, que por
    // sua vez depende de o app dar retorno. Ou seja: pode nunca sair, e nada no
    // roadmap do produto pode ficar esperando por isso.
    //
    // Destrave SOMENTE quando as três coisas forem verdade, nesta ordem:
    //   1. o gerente de contas do Asaas confirmar o BaaS habilitado em PRODUÇÃO
    //      (sem isso, criar subconta manda e-mail do Asaas ao prestador e
    //      entrega o painel a ele — a marca branca total é proibida pela
    //      Resolução Conjunta 16/17 de qualquer forma, hurdle #165b);
    //   2. existir a tela de onboarding da subconta — sem ela o interruptor é
    //      beco sem saída (liga, cai em `no_account` e não há como sair de lá);
    //   3. o piloto estar dimensionado para a janela regulatória (#165c).
    // Só então: `free: 'upsell'` + `pro: 'allowed'` aqui, `'pro'` na lista do
    // servidor (`_shared/entitlement.ts`) e o tripwire de
    // `paymentDetection.test.ts` — os três mudam JUNTOS ou o guard reprova.
    //
    // Enquanto isso, NADA do que existe hoje muda para o usuário: o Pix estático
    // continua grátis para todo mundo, o card nem é renderizado, e as edge
    // functions recusam com 402 antes de tocar no Asaas.
    free: 'blocked',
    pro: 'blocked',
    admin: 'allowed',
    upsellReason: 'feature',
  },
} satisfies Record<FeatureId, FeatureRule>;

/**
 * Acesso de uma feature no plano informado. Fail-closed: plano desconhecido cai
 * no plano GRÁTIS, que é o mais restritivo.
 *
 * A checagem de propriedade PRÓPRIA substitui o `rule[planType] ?? rule.free`:
 * com o acesso direto, um nome herdado do protótipo (`'toString'`,
 * `'constructor'`) NÃO é `undefined` — é a função herdada de Object.prototype —,
 * então o `??` não disparava e a função devolvia uma `Function` tipada como
 * `FeatureAccess`. (`hasOwnProperty.call` e não `Object.hasOwn` porque a lib
 * de TypeScript deste projeto é anterior a ES2022.)
 *
 * Hoje isso é inofensivo porque `useFeature` compara com `=== 'allowed'` (há
 * guard de varredura exigindo a comparação POSITIVA). Mas é o tipo de buraco
 * que vira brecha no dia em que alguém "simplificar" para `!== 'blocked'`.
 */
export function getFeatureAccess(id: FeatureId, planType: PlanType): FeatureAccess {
  const rule = FEATURES[id];
  return Object.prototype.hasOwnProperty.call(rule, planType) ? rule[planType] : rule.free;
}
